import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    Typography,
} from "@mui/material";
import {
    AddCircleOutline,
    Assessment,
    Event,
    PictureAsPdf,
    ReceiptLong,
    RemoveCircleOutline,
    SwapHoriz,
    TrendingUp,
} from "@mui/icons-material";
import { API } from "../../../lib/api";

const LIVE_FORECAST_VALUE = "live-forecast";

const MOVEMENT_COLUMNS = ["Opportunity", "Customer", "Product Group", "Owner", "Weighted Amount", "Ship Date", "Booked Orders Stage"];

const comparisonSections = [
    {
        key: "movedIn",
        title: "Moved Into Forecast",
        icon: AddCircleOutline,
        tone: "success",
        amountColor: "success.main",
        columns: MOVEMENT_COLUMNS,
    },
    {
        key: "movedOut",
        title: "Moved Out of Forecast",
        icon: RemoveCircleOutline,
        tone: "error",
        amountColor: "error.main",
        columns: MOVEMENT_COLUMNS,
    },
    {
        key: "shipDateChanges",
        title: "Ship Date Changes",
        icon: Event,
        columns: ["Opportunity", "Customer", "Project #", "Product Group", "Previous Ship Date", "New Ship Date", "Weighted Amount"],
    },
    {
        key: "statusChanges",
        title: "Booked Orders Stage Changes",
        icon: SwapHoriz,
        columns: ["Opportunity", "Customer", "Product Group", "Previous Booked Orders Stage", "New Booked Orders Stage", "Weighted Amount"],
    },
    {
        key: "purchaseOrders",
        title: "PO's",
        icon: ReceiptLong,
        columns: ["Opportunity", "Customer", "PO #", "Order Date", "Amount", "Status"],
        pendingData: true,
        emptyText: "PO comparison pending order date data.",
    },
];

const emptyReport = {
    selectedWeighted: 0,
    comparisonWeighted: 0,
    finalDelta: 0,
    netOpportunityChanges: 0,
    movedInWeighted: 0,
    movedOutWeighted: 0,
    rows: {
        movedIn: [],
        movedOut: [],
        shipDateChanges: [],
        statusChanges: [],
        purchaseOrders: [],
    },
};

const formatCurrency = (value) => Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
});

const normalizeId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (value?._id) return normalizeId(value._id);
    if (value?.id) return normalizeId(value.id);
    return String(value);
};

const getLineComparisonKey = (line) => {
    const lineId = normalizeId(line?.lineId);
    if (lineId) return lineId;

    return [
        normalizeId(line?.opportunityId),
        line?.projectNumber || "",
        line?.quoteNumber || "",
        line?.productGroup || "",
        line?.amount || "",
    ].join("|");
};

const getDate = (value) => {
    if (!value) return null;

    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getYearFromDate = (value) => getDate(value)?.getFullYear() || null;

const getYearFromTitle = (title = "") => {
    const match = String(title).match(/\b(20\d{2}|19\d{2})\b/);
    return match ? Number(match[1]) : null;
};

const getAnchorYear = (forecast) => (
    getYearFromDate(forecast?.createdAt)
    || getYearFromTitle(forecast?.title)
    || null
);

const getDateKey = (value) => {
    const date = getDate(value);
    return date ? date.toISOString().split("T")[0] : "";
};

const getDateMonthKey = (value) => {
    const date = getDate(value);
    return date ? date.toISOString().slice(0, 7) : "";
};

const formatDate = (value) => getDateKey(value);

const formatOwner = (owner) => {
    if (!owner) return "Unassigned";
    if (typeof owner === "string") return owner;

    const name = `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim();
    return name || "Unassigned";
};

const getBookedOrdersStage = (line) => line?.bookedOrdersStage || "";

const getBookedOrdersStageBadgeClass = (status) => {
    if (status === "01 - Assign Resources") return "bg-light-primary";
    if (status === "02 - Submittals Sent") return "bg-light-dark";
    if (status === "03 - Submittals Approved") return "bg-light-secondary";
    if (status === "07 - Revenue Taken") return "bg-light-success";
    if (status === "04 - Shipped") return "bg-light-warning";
    if (status === "05 - Installed") return "bg-light-danger";
    if (status === "06 - Commissioned") return "bg-light-danger";
    if (status === "10 - Closed") return "bg-light-success";
    return "bg-light-info";
};

const getBookedOrdersStageMeta = (line) => ({
    value: line?.bookedOrdersStage || "",
    badgeClass: getBookedOrdersStageBadgeClass(line?.bookedOrdersStage),
});

const buildStatusBadge = ({ value, badgeClass }) => {
    if (!value) return "";

    return (
        <label
            className={`badge ${badgeClass}`}
            style={{
                minWidth: "75%",
                padding: "6px 8px",
                lineHeight: 1.2,
                fontSize: "12px",
                whiteSpace: "normal",
            }}
        >
            {value}
        </label>
    );
};

const extractCellText = (value) => {
    if (value === null || value === undefined || value === false) return "";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (Array.isArray(value)) return value.map(extractCellText).join("");
    if (value?.props?.children !== undefined) return extractCellText(value.props.children);
    return String(value);
};

const buildPdfSections = (report) => (
    comparisonSections.map((section) => {
        const rows = report.rows?.[section.key] || [];

        return {
            key: section.key,
            title: section.title,
            columns: section.columns,
            pendingData: section.pendingData === true,
            emptyText: section.emptyText || "No rows",
            rows: rows.map((row) => (
                section.columns.reduce((acc, column) => {
                    acc[column] = extractCellText(row[column]);
                    return acc;
                }, {})
            )),
        };
    })
);

const sanitizeFileNamePart = (value = "") =>
    String(value || "")
        .replace(/[<>:"/\\|?*]+/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 80);

const buildPdfFileName = (selectedForecastLabel, comparisonForecastLabel) => {
    const selected = sanitizeFileNamePart(selectedForecastLabel || "Selected Forecast");
    const comparison = sanitizeFileNamePart(comparisonForecastLabel || "Comparison Forecast");

    return `Forecast Comparison - ${selected} vs ${comparison}.pdf`;
};

const downloadPdfBuffer = (data, fileName) => {
    const blob = new Blob([data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const sumWeightedAmount = (lines = []) =>
    lines.reduce((sum, line) => sum + (Number(line?.weightedAmount) || 0), 0);

const filterLinesByYear = (lines = [], anchorYear, compareAllShipDates) => {
    if (compareAllShipDates || !anchorYear) return lines;
    return lines.filter((line) => getYearFromDate(line?.shipDate) === anchorYear);
};

const buildLineMap = (lines = []) => {
    const map = new Map();

    lines.forEach((line) => {
        const key = getLineComparisonKey(line);
        if (key) map.set(key, line);
    });

    return map;
};

const buildOpportunityCell = (line) => {
    const opportunityName = line?.opportunityName || "Untitled Opportunity";
    const opportunityId = normalizeId(line?.opportunityId);

    if (!opportunityId) return opportunityName;

    return (
        <Typography
            component="a"
            href={`/opportunities/${opportunityId}`}
            sx={{
                color: "primary.main",
                fontSize: "inherit",
                fontWeight: 600,
                lineHeight: "inherit",
                textDecoration: "none",
                "&:hover": {
                    color: "primary.dark",
                    textDecoration: "none",
                },
            }}
        >
            {opportunityName}
        </Typography>
    );
};

const buildAmountCell = (value, color = "text.primary") => (
    <Typography component="span" sx={{ color, fontSize: "inherit", fontWeight: 600, lineHeight: "inherit" }}>
        {formatCurrency(value)}
    </Typography>
);

const buildMovementRow = (line, amountColor) => ({
    id: getLineComparisonKey(line),
    Opportunity: buildOpportunityCell(line),
    Customer: line?.customer || "",
    "Product Group": line?.productGroup || "",
    Owner: formatOwner(line?.owner),
    "Weighted Amount": buildAmountCell(line?.weightedAmount, amountColor),
    "Ship Date": formatDate(line?.shipDate),
    "Booked Orders Stage": buildStatusBadge(getBookedOrdersStageMeta(line)),
});

const buildChangeBase = (line) => ({
    id: getLineComparisonKey(line),
    Opportunity: buildOpportunityCell(line),
    Customer: line?.customer || "",
    "Project #": line?.projectNumber || "",
    "Product Group": line?.productGroup || "",
    "Weighted Amount": formatCurrency(line?.weightedAmount),
});

const compareForecastLines = (selectedLines = [], comparisonLines = []) => {
    const selectedMap = buildLineMap(selectedLines);
    const comparisonMap = buildLineMap(comparisonLines);
    const movedIn = [];
    const movedOut = [];
    const shipDateChanges = [];
    const statusChanges = [];
    let movedInWeighted = 0;
    let movedOutWeighted = 0;

    comparisonMap.forEach((comparisonLine, key) => {
        const selectedLine = selectedMap.get(key);

        if (!selectedLine) {
            movedInWeighted += Number(comparisonLine?.weightedAmount) || 0;
            movedIn.push(buildMovementRow(comparisonLine, "success.main"));
            return;
        }

        const selectedShipDate = getDateKey(selectedLine?.shipDate);
        const comparisonShipDate = getDateKey(comparisonLine?.shipDate);
        const selectedShipMonth = getDateMonthKey(selectedLine?.shipDate);
        const comparisonShipMonth = getDateMonthKey(comparisonLine?.shipDate);
        if (selectedShipMonth !== comparisonShipMonth) {
            shipDateChanges.push({
                ...buildChangeBase(comparisonLine),
                "Project #": comparisonLine?.projectNumber || selectedLine?.projectNumber || "",
                "Previous Ship Date": selectedShipDate || "No Date",
                "New Ship Date": comparisonShipDate || "No Date",
            });
        }

        const selectedBookedOrdersStage = getBookedOrdersStage(selectedLine);
        const comparisonBookedOrdersStage = getBookedOrdersStage(comparisonLine);
        if (selectedBookedOrdersStage !== comparisonBookedOrdersStage) {
            statusChanges.push({
                ...buildChangeBase(comparisonLine),
                "Previous Booked Orders Stage": buildStatusBadge(getBookedOrdersStageMeta(selectedLine)) || "No Booked Orders Stage",
                "New Booked Orders Stage": buildStatusBadge(getBookedOrdersStageMeta(comparisonLine)) || "No Booked Orders Stage",
            });
        }
    });

    selectedMap.forEach((selectedLine, key) => {
        if (!comparisonMap.has(key)) {
            movedOutWeighted += Number(selectedLine?.weightedAmount) || 0;
            movedOut.push(buildMovementRow(selectedLine, "error.main"));
        }
    });

    const selectedWeighted = sumWeightedAmount(selectedLines);
    const comparisonWeighted = sumWeightedAmount(comparisonLines);
    const finalDelta = comparisonWeighted - selectedWeighted;

    return {
        selectedWeighted,
        comparisonWeighted,
        finalDelta,
        netOpportunityChanges: movedIn.length - movedOut.length,
        movedInWeighted,
        movedOutWeighted,
        rows: {
            movedIn,
            movedOut,
            shipDateChanges,
            statusChanges,
            purchaseOrders: [],
        },
    };
};

const getDeltaColor = (value) => {
    if (value > 0) return "success.main";
    if (value < 0) return "error.main";
    return "primary.main";
};

const getDeltaCardStyles = (value) => {
    if (value > 0) {
        return {
            borderColor: "success.light",
            bgcolor: "rgba(46, 125, 50, 0.06)",
            color: "success.main",
        };
    }

    if (value < 0) {
        return {
            borderColor: "error.light",
            bgcolor: "rgba(211, 47, 47, 0.06)",
            color: "error.main",
        };
    }

    return {
        borderColor: "primary.light",
        bgcolor: "rgba(25, 118, 210, 0.06)",
        color: "primary.main",
    };
};

const getColumnCellSx = (column) => {
    if (column === "Opportunity") {
        return { minWidth: 220, maxWidth: 300 };
    }

    if (["Status", "Booked Orders Stage", "Previous Booked Orders Stage", "New Booked Orders Stage"].includes(column)) {
        return { minWidth: 160, maxWidth: 220 };
    }

    if (column === "Project #") {
        return { minWidth: 120, whiteSpace: "nowrap" };
    }

    if (["Weighted Amount", "Amount"].includes(column)) {
        return { minWidth: 140, whiteSpace: "nowrap" };
    }

    if (column.includes("Date")) {
        return { minWidth: 130, whiteSpace: "nowrap" };
    }

    return { minWidth: 140 };
};

const ReportSectionTable = ({
    columns,
    rows = [],
    rowTextColor,
    emptyText = "No rows",
    enablePagination = true,
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        setPage(0);
    }, [rows.length, rowsPerPage]);

    const visibleRows = enablePagination
        ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : rows;

    return (
        <Box>
            <TableContainer sx={{ maxHeight: enablePagination ? 520 : "none", overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell
                                    key={column}
                                    sx={{
                                        ...getColumnCellSx(column),
                                        bgcolor: "#f8fafc",
                                        color: "text.secondary",
                                        fontWeight: 700,
                                        py: 1,
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {column}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.length ? (
                            visibleRows.map((row, rowIndex) => (
                                <TableRow
                                    key={row.id || rowIndex}
                                    hover
                                    sx={{
                                        "&:nth-of-type(even)": {
                                            bgcolor: "rgba(15, 23, 42, 0.025)",
                                        },
                                    }}
                                >
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column}
                                            sx={{
                                                ...getColumnCellSx(column),
                                                color: rowTextColor || "text.primary",
                                                fontSize: "0.875rem",
                                                lineHeight: 1.35,
                                                py: 1,
                                                verticalAlign: "top",
                                            }}
                                        >
                                            {row[column] || ""}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length} sx={{ color: "text.secondary", height: 88, textAlign: "center" }}>
                                    {emptyText}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {enablePagination && rows.length > 0 && (
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0);
                    }}
                    sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                />
            )}
        </Box>
    );
};

const ReportingTool = ({ liveLines = [] }) => {
    const [forecasts, setForecasts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [primaryForecastId, setPrimaryForecastId] = useState("");
    const [comparisonForecastId, setComparisonForecastId] = useState("");
    const [compareAllShipDates, setCompareAllShipDates] = useState(false);
    const [reportGenerated, setReportGenerated] = useState(false);
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [pdfError, setPdfError] = useState("");
    const [activeSectionKey, setActiveSectionKey] = useState(comparisonSections[0].key);

    const forecastOptions = useMemo(() => {
        return [...forecasts].sort((a, b) => {
            const aDate = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bDate = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bDate - aDate;
        });
    }, [forecasts]);

    useEffect(() => {
        async function fetchForecasts() {
            const response = await API.Forecasting.FetchAll();
            setForecasts(Array.isArray(response) ? response : []);
            setLoading(false);
        }

        fetchForecasts();
    }, []);

    const primaryForecast = useMemo(
        () => forecastOptions.find((forecast) => forecast._id === primaryForecastId) || null,
        [forecastOptions, primaryForecastId]
    );

    const comparisonForecast = useMemo(
        () => forecastOptions.find((forecast) => forecast._id === comparisonForecastId) || null,
        [forecastOptions, comparisonForecastId]
    );

    const anchorYear = useMemo(() => getAnchorYear(primaryForecast), [primaryForecast]);

    const selectedForecastLabel = primaryForecast?.title || "Selected Forecast";
    const comparisonForecastLabel = comparisonForecastId === LIVE_FORECAST_VALUE
        ? "Live Forecast"
        : comparisonForecast?.title || "Comparison Forecast";
    const canGenerateReport = Boolean(primaryForecastId && comparisonForecastId && (compareAllShipDates || anchorYear));

    const selectedLines = useMemo(() => {
        return filterLinesByYear(primaryForecast?.lines || [], anchorYear, compareAllShipDates);
    }, [anchorYear, compareAllShipDates, primaryForecast]);

    const comparisonLines = useMemo(() => {
        const sourceLines = comparisonForecastId === LIVE_FORECAST_VALUE
            ? liveLines
            : comparisonForecast?.lines || [];

        return filterLinesByYear(sourceLines, anchorYear, compareAllShipDates);
    }, [anchorYear, compareAllShipDates, comparisonForecast, comparisonForecastId, liveLines]);

    const currentReport = useMemo(() => {
        if (!canGenerateReport) return emptyReport;
        return compareForecastLines(selectedLines, comparisonLines);
    }, [canGenerateReport, selectedLines, comparisonLines]);

    const displayedReport = reportGenerated ? currentReport : emptyReport;
    const deltaStyles = getDeltaCardStyles(displayedReport.finalDelta);
    const movedInStyles = {
        borderColor: "success.light",
        bgcolor: "rgba(46, 125, 50, 0.06)",
        color: "success.main",
    };
    const movedOutStyles = {
        borderColor: "error.light",
        bgcolor: "rgba(211, 47, 47, 0.06)",
        color: "error.main",
    };
    const shipDateScopeLabel = compareAllShipDates
        ? "All Ship Dates"
        : anchorYear
            ? `Forecast Year: ${anchorYear}`
            : "Forecast Year: Unavailable";

    const reportSummaryCards = [
        { label: "Selected Weighted", value: formatCurrency(displayedReport.selectedWeighted) },
        { label: "Comparison Weighted", value: formatCurrency(displayedReport.comparisonWeighted) },
        {
            label: "Final Number Delta",
            value: formatCurrency(displayedReport.finalDelta),
            borderColor: deltaStyles.borderColor,
            bgcolor: deltaStyles.bgcolor,
            color: deltaStyles.color,
        },
        {
            label: "Moved Into Forecast",
            value: formatCurrency(displayedReport.movedInWeighted),
            borderColor: movedInStyles.borderColor,
            bgcolor: movedInStyles.bgcolor,
            color: movedInStyles.color,
        },
        {
            label: "Moved Out of Forecast",
            value: formatCurrency(displayedReport.movedOutWeighted),
            borderColor: movedOutStyles.borderColor,
            bgcolor: movedOutStyles.bgcolor,
            color: movedOutStyles.color,
        },
        { label: "Net Opportunity Changes", value: String(displayedReport.netOpportunityChanges) },
    ];

    const activeSectionIndex = Math.max(
        comparisonSections.findIndex((section) => section.key === activeSectionKey),
        0
    );
    const activeSection = comparisonSections[activeSectionIndex];
    const ActiveSectionIcon = activeSection.icon;
    const activeSectionRows = displayedReport.rows[activeSection.key] || [];

    const handleActiveSectionChange = (event, nextIndex) => {
        setActiveSectionKey(comparisonSections[nextIndex].key);
    };

    const renderForecastOptions = (disabledForecastId = "") => (
        forecastOptions.map((forecast) => (
            <MenuItem key={forecast._id} value={forecast._id} disabled={forecast._id === disabledForecastId}>
                {forecast.title || "Untitled Forecast"}
            </MenuItem>
        ))
    );

    const resetReport = () => {
        setReportGenerated(false);
        setPdfError("");
    };

    const handlePrimaryForecastChange = (event) => {
        const nextForecastId = event.target.value;

        setPrimaryForecastId(nextForecastId);
        if (comparisonForecastId === nextForecastId) {
            setComparisonForecastId("");
        }
        resetReport();
    };

    const handleComparisonForecastChange = (event) => {
        const nextForecastId = event.target.value;

        if (nextForecastId === primaryForecastId) {
            setComparisonForecastId("");
            resetReport();
            return;
        }

        setComparisonForecastId(nextForecastId);
        resetReport();
    };

    const handleCompareAllShipDatesChange = (event) => {
        setCompareAllShipDates(event.target.checked);
        resetReport();
    };

    const handleGenerateReport = () => {
        setPdfError("");
        setReportGenerated(true);
    };

    const handleGeneratePdf = async () => {
        if (!reportGenerated || pdfGenerating) return;

        setPdfGenerating(true);
        setPdfError("");

        const reportPayload = {
            selectedForecastLabel,
            comparisonForecastLabel,
            shipDateScopeLabel,
            generatedAt: new Date().toISOString(),
            status: "Generated",
            summary: {
                selectedWeighted: displayedReport.selectedWeighted,
                comparisonWeighted: displayedReport.comparisonWeighted,
                finalDelta: displayedReport.finalDelta,
                netOpportunityChanges: displayedReport.netOpportunityChanges,
                movedInWeighted: displayedReport.movedInWeighted,
                movedOutWeighted: displayedReport.movedOutWeighted,
            },
            sections: buildPdfSections(displayedReport),
        };

        const response = await API.Forecasting.GenerateComparisonPdf(reportPayload);

        if (response.status === 200) {
            downloadPdfBuffer(
                response.data,
                buildPdfFileName(selectedForecastLabel, comparisonForecastLabel)
            );
        } else {
            setPdfError("Forecast comparison PDF failed to generate.");
        }

        setPdfGenerating(false);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden;
                        }

                        .forecast-report-print-area,
                        .forecast-report-print-area * {
                            visibility: visible;
                        }

                        .forecast-report-print-area {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                        }

                        .forecast-report-no-print {
                            display: none !important;
                        }

                        .forecast-report-screen-details {
                            display: none !important;
                        }

                        .forecast-report-print-only {
                            display: flex !important;
                            flex-direction: column;
                            gap: 16px;
                        }
                    }
                `}
            </style>

            <Paper
                elevation={0}
                className="forecast-report-no-print"
                sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(260px, 1fr)) auto" },
                        alignItems: "center",
                    }}
                >
                    <FormControl fullWidth>
                        <InputLabel id="primary-forecast-label">Forecast</InputLabel>
                        <Select
                            labelId="primary-forecast-label"
                            id="primary-forecast-select"
                            value={primaryForecastId}
                            label="Forecast"
                            onChange={handlePrimaryForecastChange}
                        >
                            <MenuItem value="" disabled>
                                Select a forecast
                            </MenuItem>
                            {renderForecastOptions(comparisonForecastId)}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel id="comparison-forecast-label">Compare To</InputLabel>
                        <Select
                            labelId="comparison-forecast-label"
                            id="comparison-forecast-select"
                            value={comparisonForecastId}
                            label="Compare To"
                            onChange={handleComparisonForecastChange}
                        >
                            <MenuItem value="" disabled>
                                Select a forecast
                            </MenuItem>
                            <MenuItem value={LIVE_FORECAST_VALUE}>Live Forecast</MenuItem>
                            {renderForecastOptions(primaryForecastId)}
                        </Select>
                    </FormControl>

                    <Button
                        variant="contained"
                        startIcon={<Assessment />}
                        onClick={handleGenerateReport}
                        disabled={!canGenerateReport}
                        sx={{
                            height: 56,
                            whiteSpace: "nowrap",
                        }}
                    >
                        Generate Report
                    </Button>
                </Box>

                <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                    <Chip label={shipDateScopeLabel} color={compareAllShipDates ? "default" : "primary"} variant="outlined" />
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={compareAllShipDates}
                                onChange={handleCompareAllShipDatesChange}
                            />
                        )}
                        label="Compare all ship dates"
                    />
                </Stack>
            </Paper>

            <Box className="forecast-report-print-area" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={2}
                        alignItems={{ xs: "stretch", md: "center" }}
                        justifyContent="space-between"
                        sx={{ p: 2 }}
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: "wrap" }}>
                                <Typography variant="h5">Forecast Change Report</Typography>
                                <Chip
                                    label={reportGenerated ? "Generated" : "Draft"}
                                    color={reportGenerated ? "success" : "default"}
                                    size="small"
                                />
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
                                <Chip label={selectedForecastLabel} color="primary" variant="outlined" />
                                <Typography color="text.secondary">vs</Typography>
                                <Chip label={comparisonForecastLabel} color="primary" variant="outlined" />
                                <Chip label={shipDateScopeLabel} variant="outlined" />
                            </Stack>
                        </Box>

                        <Button
                            className="forecast-report-no-print"
                            variant="outlined"
                            startIcon={<PictureAsPdf />}
                            onClick={handleGeneratePdf}
                            disabled={!reportGenerated || pdfGenerating}
                            sx={{ height: 40, whiteSpace: "nowrap" }}
                        >
                            {pdfGenerating ? "Generating PDF..." : "Generate PDF"}
                        </Button>
                    </Stack>
                    {pdfError && (
                        <Typography className="forecast-report-no-print" color="error" sx={{ px: 2, pb: 1 }}>
                            {pdfError}
                        </Typography>
                    )}

                    <Divider />

                    <Box
                        sx={{
                            display: "grid",
                            gap: 2,
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)", xl: "repeat(6, 1fr)" },
                            p: 2,
                        }}
                    >
                        {reportSummaryCards.map((card) => (
                            <Box
                                key={card.label}
                                sx={{
                                    border: "1px solid",
                                    borderColor: card.borderColor || "divider",
                                    borderRadius: 1,
                                    p: 2,
                                    minHeight: 96,
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    bgcolor: card.bgcolor || "background.paper",
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {card.label}
                                </Typography>
                                <Typography variant="h5" color={card.color || "text.primary"}>
                                    {card.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    <Divider />

                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <TrendingUp color="primary" />
                            <Typography variant="h6">Final Number Delta</Typography>
                        </Stack>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr auto 1fr" },
                                gap: 2,
                                alignItems: "center",
                            }}
                        >
                            <Box>
                                <Typography variant="body2" color="text.secondary">Comparison Forecast Weighted</Typography>
                                <Typography variant="h6">{formatCurrency(displayedReport.comparisonWeighted)}</Typography>
                            </Box>
                            <Typography color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>-</Typography>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Selected Forecast Weighted</Typography>
                                <Typography variant="h6">{formatCurrency(displayedReport.selectedWeighted)}</Typography>
                            </Box>
                            <Typography color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>=</Typography>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Delta</Typography>
                                <Typography variant="h6" color={getDeltaColor(displayedReport.finalDelta)}>
                                    {formatCurrency(displayedReport.finalDelta)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                <Paper
                    elevation={0}
                    className="forecast-report-screen-details"
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        overflow: "hidden",
                    }}
                >
                    <Tabs
                        value={activeSectionIndex}
                        onChange={handleActiveSectionChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 56,
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            bgcolor: "#fbfcfe",
                        }}
                    >
                        {comparisonSections.map((section) => {
                            const SectionIcon = section.icon;
                            const sectionRows = displayedReport.rows[section.key] || [];

                            return (
                                <Tab
                                    key={section.key}
                                    icon={<SectionIcon color={section.tone || "primary"} />}
                                    iconPosition="start"
                                    label={(
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography
                                                variant="body2"
                                                sx={{ color: section.rowTextColor || "text.primary", fontWeight: 700 }}
                                            >
                                                {section.title}
                                            </Typography>
                                            <Chip
                                                label={section.pendingData ? "Pending" : sectionRows.length}
                                                size="small"
                                                color={section.tone || "default"}
                                                variant={section.pendingData ? "outlined" : "filled"}
                                            />
                                        </Stack>
                                    )}
                                    sx={{ minHeight: 56, textTransform: "none" }}
                                />
                            );
                        })}
                    </Tabs>

                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        justifyContent="space-between"
                        sx={{ px: 2, py: 1.5 }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <ActiveSectionIcon color={activeSection.tone || "primary"} />
                            <Typography variant="h6" color={activeSection.rowTextColor || "text.primary"}>
                                {activeSection.title}
                            </Typography>
                        </Stack>
                        <Chip
                            label={activeSection.pendingData ? "Pending Data" : `${activeSectionRows.length} Row${activeSectionRows.length === 1 ? "" : "s"}`}
                            size="small"
                            color={activeSection.tone || "default"}
                            variant={activeSection.pendingData ? "outlined" : "filled"}
                        />
                    </Stack>
                    <Divider />
                    <ReportSectionTable
                        columns={activeSection.columns}
                        rows={activeSectionRows}
                        rowTextColor={activeSection.rowTextColor}
                        emptyText={activeSection.emptyText}
                    />
                </Paper>

                <Box className="forecast-report-print-only" sx={{ display: "none" }}>
                    {comparisonSections.map((section) => {
                        const SectionIcon = section.icon;
                        const sectionRows = displayedReport.rows[section.key] || [];

                        return (
                            <Paper
                                key={section.key}
                                elevation={0}
                                sx={{
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                    overflow: "hidden",
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={2}
                                    sx={{ p: 2 }}
                                >
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <SectionIcon color={section.tone || "primary"} />
                                        <Typography variant="h6" color={section.rowTextColor || "text.primary"}>
                                            {section.title}
                                        </Typography>
                                    </Stack>
                                    <Chip
                                        label={section.pendingData ? "Pending" : sectionRows.length}
                                        size="small"
                                        color={section.tone || "default"}
                                        variant={section.pendingData ? "outlined" : "filled"}
                                    />
                                </Stack>
                                <Divider />
                                <ReportSectionTable
                                    columns={section.columns}
                                    rows={sectionRows}
                                    rowTextColor={section.rowTextColor}
                                    emptyText={section.emptyText}
                                    enablePagination={false}
                                />
                            </Paper>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default ReportingTool;
